/**
 * The label that goes on the bin.
 *
 * Black on white whatever the theme and whatever the language, because that is
 * what a scanner reads: a barcode rendered in a dark theme's greys, or
 * mirrored by the right-to-left layout, is a barcode that does not scan.
 *
 * It carries the catalogue number as a Code 128 symbol for the handset on the
 * counter, and the same number in readable characters underneath — because
 * every scanner fails eventually and somebody then has to read it out.
 */
import { bars, code128 } from "../../domain/barcode";
import type { StockItem, StoreLocation } from "../../domain/stores";
import { t } from "../../i18n";
import s from "./Stores.module.css";

export function BinLabel({
  item,
  locations,
  width = 260,
  height = 56,
}: {
  item: StockItem;
  locations: StoreLocation[];
  width?: number;
  height?: number;
}) {
  /* the barcode on the shelf if there is one, else the catalogue number */
  const text = item.barcode ?? item.sku;
  const code = code128(text);
  const bin = item.bins[0];
  const store = locations.find((l) => l.code === bin?.location);

  return (
    <div className={s.label} data-testid="bin-label" dir="ltr">
      <div className={s.labelName}>{item.name}</div>
      {code ? (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={t("Barcode for {code}", { code: text })}
        >
          <rect x={0} y={0} width={width} height={height} fill="#fff" />
          {bars(code, width, height).map((b, i) => (
            <rect key={i} x={b.x} y={0} width={b.w} height={b.h} fill="#000" />
          ))}
        </svg>
      ) : (
        <div className={s.labelWhere}>{t("This number cannot be drawn as a Code 128 symbol.")}</div>
      )}
      <div className={s.labelCode}>{text}</div>
      {(store || bin?.bin) && (
        <div className={s.labelWhere}>
          {[store?.name, bin?.bin].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}
